import React, { useEffect, useRef } from 'react';
import { Form, Formik } from 'formik';
import { camelCase } from 'lodash';
import * as Yup from 'yup';
import styled from 'styled-components';
import { Box, Button, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@material-ui/core';
import { AppSnackbar } from '@sierralabs/core-ui';
import { EntityNumericPlugin, useEntityContext } from '@sierralabs/entity-ui';
import { FormikCheckboxField, FormikGrid, FormikSelectField, FormikTextField } from '@sierralabs/react-material-ui-formik';
import { Alert } from '@material-ui/lab';
import { useFormContext } from '../FormContext';
import { schemaDataTypes } from '../schema/schemaDataTypes';
const StyledFormikGrid = styled(FormikGrid) `
  padding-bottom: 8px;

  .numeric-grid .MuiGrid-container .MuiGrid-item {
    /* Always make the first Grid item span full width (xs={12}) */
    &:first-child {
      flex-basis: 100%;
      max-width: 100%;
    }

    ${props => props.theme.breakpoints.up('sm')} {
      &:not(:first-child) {
        flex-basis: 50% !important;
        max-width: 50% !important;
      }
    }
  }
`;
const FormSchemaFieldCreateDialog = ({ open, column, onCancel, onAdded }) => {
    const formikRef = useRef(null);
    const mountRef = useRef(false);
    const onAddedRef = useRef(onAdded);
    const { formState, entitySchema, elementPlugins } = useFormContext();
    const { isUpdating, selectedEntity, plugins, updateError, updatedResult, trackChanges, setSelectedEntity } = useEntityContext();
    const element = formState.selectedFormElement;
    const plugin = elementPlugins.find(p => p.type === element.type);
    // close dialog if entity schema gets updated
    useEffect(() => {
        var _a;
        if (entitySchema &&
            updatedResult &&
            mountRef.current &&
            formikRef.current) {
            (_a = onAddedRef.current) === null || _a === void 0 ? void 0 : _a.call(onAddedRef, formikRef.current.values.name);
        }
        mountRef.current = true;
    }, [entitySchema, updatedResult]);
    // select entity being edited
    useEffect(() => {
        if (entitySchema)
            setSelectedEntity(entitySchema);
    }, [entitySchema, setSelectedEntity]);
    useEffect(() => {
        var _a;
        (_a = formikRef.current) === null || _a === void 0 ? void 0 : _a.setSubmitting(isUpdating);
    }, [isUpdating]);
    return (React.createElement(Dialog, { open: open, onClose: onCancel, fullWidth: true },
        React.createElement(DialogTitle, null,
            column ? 'Edit' : 'Add',
            " Entity Schema Field"),
        React.createElement(Formik, { validateOnMount: true, innerRef: formikRef, initialValues: {
                displayName: (column === null || column === void 0 ? void 0 : column.displayName) || '',
                name: (column === null || column === void 0 ? void 0 : column.name) || '',
                type: (column === null || column === void 0 ? void 0 : column.type) ||
                    ((plugin === null || plugin === void 0 ? void 0 : plugin.dataTypes) && (plugin === null || plugin === void 0 ? void 0 : plugin.dataTypes.length) > 0
                        ? plugin.dataTypes[0]
                        : 'text'),
                array: (column === null || column === void 0 ? void 0 : column.array) || (plugin === null || plugin === void 0 ? void 0 : plugin.dataArray) || false,
                comment: (column === null || column === void 0 ? void 0 : column.comment) || '',
                enum: (column === null || column === void 0 ? void 0 : column.enum) || undefined,
                precision: column === null || column === void 0 ? void 0 : column.precision,
                scale: column === null || column === void 0 ? void 0 : column.scale
            }, validationSchema: Yup.object({
                displayName: Yup.string()
                    .trim()
                    .required('Field name is required')
                    .test('unique', 'Duplicate field name', value => {
                    var _a;
                    return !((_a = entitySchema === null || entitySchema === void 0 ? void 0 : entitySchema.columns) === null || _a === void 0 ? void 0 : _a.some(c => c !== column && c.displayName === value));
                }),
                type: Yup.string().required('Field type is required')
            }), onSubmit: values => {
                var _a;
                values.name = camelCase(values.displayName);
                console.log('onSubmit values', values);
                if (column) {
                    const index = ((_a = entitySchema === null || entitySchema === void 0 ? void 0 : entitySchema.columns) === null || _a === void 0 ? void 0 : _a.indexOf(column)) || -1;
                    if (index > -1) {
                        // required to handle name change in sql migration
                        const keys = Object.keys(values);
                        for (const key of keys) {
                            trackChanges.update(`columns[${index}].${key}`, values[key]);
                        }
                    }
                    else {
                        console.error('column not found', column);
                    }
                }
                else {
                    trackChanges.insert('columns', values);
                }
            } }, ({ values, isValid, isSubmitting, setFieldValue, submitForm }) => {
            var _a;
            const dataType = values.type;
            const entityPlugin = plugins.find(plugin => plugin.type === dataType);
            const Configurator = entityPlugin === null || entityPlugin === void 0 ? void 0 : entityPlugin.configurator;
            const entityValidationError = (_a = entityPlugin === null || entityPlugin === void 0 ? void 0 : entityPlugin.validate) === null || _a === void 0 ? void 0 : _a.call(entityPlugin, values);
            const disabled = !isValid || isSubmitting || !!entityValidationError;
            const picker = (React.createElement(FormikSelectField, { name: 'type', label: 'Data Type', options: (plugin === null || plugin === void 0 ? void 0 : plugin.dataTypes) || schemaDataTypes, onChange: event => {
                    const value = event.target.value;
                    if (value === 'enum') {
                        setFieldValue('enum', [], true);
                    }
                    setFieldValue('type', value, true);
                }, helperText: (plugin === null || plugin === void 0 ? void 0 : plugin.dataTypes)
                    ? `${plugin.name} restricts to only specific data types (${plugin.dataTypes.join(', ')})`
                    : '' }));
            return (React.createElement(React.Fragment, null,
                React.createElement(DialogContent, null,
                    React.createElement(Form, { autoComplete: 'off' },
                        React.createElement(StyledFormikGrid, null,
                            React.createElement(FormikTextField, { name: 'displayName', label: 'Field Name' }),
                            React.createElement(Grid, { item: true, xs: 12 },
                                React.createElement(TextField, { disabled: true, fullWidth: true, variant: 'outlined', label: 'Property Key', value: camelCase(values.displayName) })),
                            Configurator && selectedEntity ? (React.createElement(Grid, { item: true, xs: 12, className: dataType === EntityNumericPlugin.type
                                    ? 'numeric-grid'
                                    : '' },
                                React.createElement(Configurator, { index: 1, column: values, entity: selectedEntity, picker: picker, onChange: (schema, noUpdate) => {
                                        if (!noUpdate) {
                                            const propertyGroups = {
                                                numeric: ['precision', 'scale'],
                                                enum: ['enum']
                                            };
                                            const properties = propertyGroups[schema.type] || [];
                                            for (const property of properties) {
                                                setFieldValue(property, schema[property], true);
                                            }
                                        }
                                    } }))) : (picker),
                            (plugin === null || plugin === void 0 ? void 0 : plugin.dataArray) && (React.createElement(FormikCheckboxField, { disabled: true, name: 'array', label: 'Array (Set database column as an array list)' })),
                            React.createElement(FormikTextField, { multiline: true, name: 'comment', label: 'Comment / Description' })))),
                React.createElement(Collapse, { in: !!entityValidationError, style: { minHeight: 'unset' } },
                    React.createElement(Box, { mx: 3 },
                        React.createElement(Alert, { className: 'error', severity: 'error' }, entityValidationError))),
                React.createElement(DialogActions, null,
                    React.createElement(Button, { onClick: onCancel }, "Cancel"),
                    React.createElement(Button, { variant: 'contained', color: 'primary', disabled: disabled, endIcon: isSubmitting && React.createElement(CircularProgress, { size: 16 }), onClick: submitForm }, "Submit"))));
        }),
        React.createElement(AppSnackbar, { open: !!updateError, severity: 'error' }, updateError === null || updateError === void 0 ? void 0 : updateError.message)));
};
export default FormSchemaFieldCreateDialog;
//# sourceMappingURL=FormSchemaFieldCreateDialog.js.map